import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload as UploadIcon, FileText, CheckCircle2, Download, Calendar, FileSpreadsheet, BookOpen, Loader2, AlertTriangle, Trash2 } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Avatar,
  Divider,
  Collapse,
  IconButton,
  Pagination,
} from '@mui/material';
import {
  CloudUpload,
  Description,
  AttachMoney,
  CalendarToday,
  Business,
  Email,
  Phone,
  LocationOn,
  AccountBalance,
  CheckCircle,
  Calculate,
  ExpandMore,
  ChevronRight,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import CompanyService from '../../services/companyService';
import AuthService from '../../services/authService';
import { useCompany } from '../../contexts/CompanyContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAppTheme } from '../../contexts/ThemeContext';
import { EDEFTER_API_URL } from '../../config/edefterApi';

const getTableHeadRowSx = (darkMode) => ({
  '& .MuiTableCell-root': {
    backgroundColor: darkMode ? '#2C2C2E' : '#f9fafb',
    color: darkMode ? '#9ca3af' : 'inherit',
    borderBottom: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f3f4f6',
    fontWeight: 700,
    zIndex: 3,
  },
});

const getTableContainerSx = (darkMode, opts = {}) => ({
  maxHeight: opts.maxHeight || 'calc(100vh - 200px)',
  overflow: 'auto',
  ...(opts.width ? { width: opts.width } : {}),
  backgroundColor: darkMode ? '#1C1C1E' : 'transparent',
  scrollbarColor: darkMode ? '#3f3f46 #1c1c1e' : undefined,
  '&::-webkit-scrollbar': darkMode ? { width: 8, height: 8 } : {},
  '&::-webkit-scrollbar-track': darkMode ? { background: '#1c1c1e' } : {},
  '&::-webkit-scrollbar-thumb': darkMode ? { background: '#3f3f46', borderRadius: 4 } : {},
  '& .MuiTableHead-root .MuiTableCell-stickyHeader': {
    backgroundColor: darkMode ? '#2C2C2E' : '#f9fafb',
  },
});

const getNestedTableHeadRowSx = (darkMode) => ({
  '& .MuiTableCell-root': {
    backgroundColor: darkMode ? '#252528' : 'rgba(25, 118, 210, 0.05)',
    color: darkMode ? '#9ca3af' : undefined,
    borderBottom: darkMode ? '1px solid rgba(255,255,255,0.08)' : undefined,
  },
});

const EdefterUpload = ({ asModal = false, open = true, onClose, onUploadSuccess }) => {
  const { selectedCompany, taxId } = useCompany();
  const { t } = useLanguage();
  const { darkMode } = useAppTheme();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [mizanData, setMizanData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [processingJobId, setProcessingJobId] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [savedDocumentId, setSavedDocumentId] = useState(null);
  const [calculatingMizan, setCalculatingMizan] = useState(false);
  const [existingDocument, setExistingDocument] = useState(null);
  const [deletingDocument, setDeletingDocument] = useState(false);
  
  // Journal entries states
  const [showJournal, setShowJournal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [journalPage, setJournalPage] = useState(1);
  const [entryHeaders, setEntryHeaders] = useState([]);
  const [entryDetailsCache, setEntryDetailsCache] = useState(new Map());
  const [loadingDetails, setLoadingDetails] = useState(new Set());
  const [loadingJournal, setLoadingJournal] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [expandedEntries, setExpandedEntries] = useState(new Set());
  const [expandedMizanRows, setExpandedMizanRows] = useState(new Set()); // Trial balance için expanded rows
  const rowsPerPage = 50;

  const resetUploadState = useCallback(() => {
    setSelectedFile(null);
    setMizanData(null);
    setLoading(false);
    setUploadProgress(0);
    setError(null);
    setProcessingJobId(null);
    setProcessingStatus(null);
    setSavedDocumentId(null);
    setCalculatingMizan(false);
    setExistingDocument(null);
    setDeletingDocument(false);
    setShowJournal(false);
    setActiveTab(0);
    setJournalPage(1);
    setEntryHeaders([]);
    setEntryDetailsCache(new Map());
    setLoadingDetails(new Set());
    setLoadingJournal(false);
    setTotalPages(0);
    setTotalElements(0);
    setExpandedEntries(new Set());
    setExpandedMizanRows(new Set());
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleModalClose = useCallback(() => {
    resetUploadState();
    onClose?.();
  }, [resetUploadState, onClose]);

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileChange({ target: { files: [file] } });
  };

  const handleDragOver = (e) => e.preventDefault();

  const hasPreviewContent = Boolean(
    savedDocumentId || (mizanData?.rows?.length > 0) || showJournal
  );

  const fetchMizanList = async () => {
    try {
      const response = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/mizan-list`);
      if (response.ok) {
        const data = await response.json();
        setMizanData(data);
      }
    } catch (err) {
      console.error('Failed to fetch trial balance list:', err);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'text/xml' && !file.name.endsWith('.xml')) {
        toast.error('Please select an XML file.');
        return;
      }
      setSelectedFile(file);
      setError(null);
      setExistingDocument(null); // Clear existing document warning when new file is selected
    }
  };

  const handleDeleteExistingDocument = async () => {
    if (!existingDocument || !existingDocument.id) {
      toast.error('No document to delete.');
      return;
    }

    setDeletingDocument(true);
    try {
      const response = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/delete-document/${existingDocument.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Existing document deleted successfully. You can now upload the new file.');
        setExistingDocument(null);
        // Alert will close automatically when existingDocument is set to null
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to delete document.');
      }
    } catch (err) {
      console.error('Error deleting document:', err);
      toast.error('Error deleting document: ' + err.message);
    } finally {
      setDeletingDocument(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
        toast.warning('Please select an XML file.');
      return;
    }

    // Zaman ölçümü başlangıcı
    const startTime = performance.now();
    let uploadStartTime = null;
    let uploadEndTime = null;
    let processingStartTime = null;
    let processingEndTime = null;
    let mizanStartTime = null;
    let mizanEndTime = null;
    
    console.log('[TIMING] ========================================');
    console.log('[TIMING] Upload and Generate Trial Balance işlemi başladı:', new Date().toISOString());
    console.log('[TIMING] ========================================');

    setLoading(true);
    setError(null);
    setUploadProgress(0);
    setProcessingJobId(null);
    setProcessingStatus(null);
    setExistingDocument(null);
    setShowJournal(false);
    setEntryHeaders([]);
    setEntryDetailsCache(new Map());
    setExpandedEntries(new Set());

    try {
      // Read file as arrayBuffer to use it multiple times (File object can only be read once)
      const arrayBuffer = await selectedFile.arrayBuffer();
      
      // Memory optimization: Only decode first 100,000 characters for pre-check
      // Company info and period info are always at the beginning of XML
      const xmlTextForPreCheck = new TextDecoder().decode(arrayBuffer.slice(0, 100000));
    
      // Extract identifier, periodStart, periodEnd, entriesType, and companyName using Regex patterns
      // Pattern matches: <prefix:tag>value</prefix:tag> or <tag>value</tag>
      const identifier = xmlTextForPreCheck.match(/<(?:[^:]+:)?identifier[^>]*>([^<]+)<\//)?.[1]?.trim();
      let periodStart = xmlTextForPreCheck.match(/<(?:[^:]+:)?periodCoveredStart[^>]*>([^<]+)<\//)?.[1]?.trim();
      let periodEnd = xmlTextForPreCheck.match(/<(?:[^:]+:)?periodCoveredEnd[^>]*>([^<]+)<\//)?.[1]?.trim();
      const entriesTypeMatch = xmlTextForPreCheck.match(/<[^:>]*:?entriesType[^>]*>([^<]+)<\/[^:>]*:?entriesType>/i);
      const entriesType = entriesTypeMatch?.[1]?.trim();
      const companyName = xmlTextForPreCheck.match(/<(?:[^:]+:)?organizationIdentifier[^>]*>([^<]+)<\//)?.[1]?.trim() ||
                          xmlTextForPreCheck.match(/<(?:[^:]+:)?organization_identifier[^>]*>([^<]+)<\//)?.[1]?.trim();
      
      // Clean date strings: XML'den gelen tarihler bazen "2024-01-01T00:00:00" formatında gelebilir
      // Sadece ilk 10 karakteri al (yyyy-MM-dd)
      if (periodStart && periodStart.length >= 10) {
        periodStart = periodStart.substring(0, 10);
      }
      if (periodEnd && periodEnd.length >= 10) {
        periodEnd = periodEnd.substring(0, 10);
      }
      
      // Validate: All three values must be present
      if (!identifier || !periodStart || !periodEnd) {
        setLoading(false);
        setError('Dosya formatı geçersiz veya eksik bilgi içeriyor. Lütfen geçerli bir XML dosyası yükleyin.');
        toast.error('Dosya formatı geçersiz veya eksik bilgi içeriyor.');
        return;
      }
      

      // Company validation: If a company is selected, check if the uploaded file belongs to that company
      if (selectedCompany && taxId) {
        const fileTaxId = identifier.trim();
        const selectedTaxId = taxId.trim();
        
        if (fileTaxId !== selectedTaxId) {
          setLoading(false);
          setError(`The uploaded file does not belong to the selected company. Please upload a file for the selected company: ${selectedCompany.name}`);
          toast.error('The file does not belong to the selected company! Please upload the correct company file.');
          return;
        }
      }
    
      // Pre-check: Check if a document with the same period already exists
      try {
        const checkResponse = await AuthService.authenticatedFetch(
          `${EDEFTER_API_URL}/check-existing?identifier=${encodeURIComponent(identifier)}&periodStart=${encodeURIComponent(periodStart)}&periodEnd=${encodeURIComponent(periodEnd)}`
        );
        
        if (checkResponse.ok) {
          const checkData = await checkResponse.json();
          if (checkData.exists) {
            // Existing document found - show warning and stop upload (MUST stop, no continue)
            setExistingDocument({
              id: checkData.id,
              uniqueId: checkData.uniqueId,
              createTime: checkData.createTime
            });
            setLoading(false);
            // Don't show toast here, Alert will be shown in UI
            return; // Stop upload immediately
          }
        } else {
          // If check fails, stop upload to prevent data inconsistency
          setLoading(false);
          setError('Could not check for existing records. Please try again.');
          toast.error('Could not check for existing records.');
          return;
        }
      } catch (checkErr) {
        console.error('Error checking existing document:', checkErr);
        // Stop upload if check fails - don't continue to prevent data inconsistency
        setLoading(false);
        setError('An error occurred while checking for existing records. Please try again.');
        toast.error('An error occurred while checking for existing records.');
        return;
      }
    
    // Create file blob for upload
    const fileBlob = new Blob([arrayBuffer], { type: 'text/xml' });
    const file = new File([fileBlob], selectedFile.name, { type: 'text/xml' });
    
    const formData = new FormData();
    formData.append('file', file);

    // Start company info extraction as a Promise (runs in parallel with upload)
    // Use xmlTextForPreCheck (already decoded) - no need to decode again
    const companyInfoExtractionPromise = (async () => {
      let companyInfoFromXml = null;
      try {
        console.log('[COMPANY] Starting XML parsing with Regex...');
        console.log('[COMPANY] Selected file:', selectedFile.name, selectedFile.size, 'bytes');
        console.log('[COMPANY] Using xmlTextForPreCheck (first 100k):', xmlTextForPreCheck.length);
        
        // Extract company info using Regex patterns from xmlTextForPreCheck (no DOMParser, no re-decode)
        // Pattern: <prefix:tag>value</prefix:tag> or <tag>value</tag>
        const unvan = xmlTextForPreCheck.match(/<(?:[^:]+:)?organizationIdentifier[^>]*>([^<]+)<\//)?.[1]?.trim() ||
                     xmlTextForPreCheck.match(/<(?:[^:]+:)?organization_identifier[^>]*>([^<]+)<\//)?.[1]?.trim();
        
        const website = xmlTextForPreCheck.match(/<(?:[^:]+:)?webSiteUrl[^>]*>([^<]+)<\//)?.[1]?.trim() ||
                       xmlTextForPreCheck.match(/<(?:[^:]+:)?web_site_url[^>]*>([^<]+)<\//)?.[1]?.trim();
        
        const businessDescription = xmlTextForPreCheck.match(/<(?:[^:]+:)?businessDescription[^>]*>([^<]+)<\//)?.[1]?.trim() ||
                                   xmlTextForPreCheck.match(/<(?:[^:]+:)?business_description[^>]*>([^<]+)<\//)?.[1]?.trim();
        
        // Parse NACE code from business_description
        let naceCode = null;
        if (businessDescription) {
          const numericValue = parseInt(businessDescription.trim());
          if (!isNaN(numericValue) && numericValue > 0) {
            naceCode = numericValue;
          } else {
            const match = businessDescription.match(/\d+/);
            if (match) {
              naceCode = parseInt(match[0]);
            }
          }
        }
        
        const city = xmlTextForPreCheck.match(/<(?:[^:]+:)?city[^>]*>([^<]+)<\//)?.[1]?.trim();
        const country = xmlTextForPreCheck.match(/<(?:[^:]+:)?country[^>]*>([^<]+)<\//)?.[1]?.trim();
        const adres = (city || country) ? [city, country].filter(Boolean).join(', ') : null;
        
        const ePosta = xmlTextForPreCheck.match(/<(?:[^:]+:)?emailAddress[^>]*>([^<]+)<\//)?.[1]?.trim() ||
                      xmlTextForPreCheck.match(/<(?:[^:]+:)?email_address[^>]*>([^<]+)<\//)?.[1]?.trim();
        
        const telefon = xmlTextForPreCheck.match(/<(?:[^:]+:)?phoneNumber[^>]*>([^<]+)<\//)?.[1]?.trim() ||
                       xmlTextForPreCheck.match(/<(?:[^:]+:)?phone_number[^>]*>([^<]+)<\//)?.[1]?.trim();
        
        // VKN/TCKN from identifier (already extracted in pre-check, reuse it)
        const vknTckn = identifier; // Use identifier from pre-check instead of re-extracting
        
        const vergiDairesi = null; // Usually not in XML
        
        console.log('[COMPANY] Extracted values:', {
          unvan,
          vknTckn,
          vergiDairesi,
          adres,
          telefon,
          ePosta,
          website,
          naceCode,
          businessDescription
        });
        
        if (unvan && vknTckn) {
          companyInfoFromXml = {
            name: unvan.trim(),
            vergiKimlikNo: vknTckn.trim(),
            vergiDairesi: vergiDairesi?.trim() || null,
            address: adres?.trim() || null,
            phone: telefon?.trim() || null,
            email: ePosta?.trim() || null,
            website: website?.trim() || null,
            naceCode: naceCode,
          };
          console.log('[COMPANY] ✅ Company info extracted from XML:', companyInfoFromXml);
        } else {
          console.warn('[COMPANY] ❌ Missing required fields - unvan:', unvan, 'vknTckn:', vknTckn);
        }
      
      return companyInfoFromXml;
    } catch (xmlErr) {
        console.error('[COMPANY] ❌ Error parsing XML:', xmlErr);
        console.error('[COMPANY] Error stack:', xmlErr.stack);
        return null;
      }
    })();

    // Start file upload asynchronously (runs in parallel with company info extraction)
    uploadStartTime = performance.now();
    console.log('[TIMING] 📤 Dosya yükleme başladı...');
    let jobId = null;
    const uploadPromise = new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        // Timeout ayarı - Büyük dosyalar için 10 dakika (600000 ms)
        // 200 MB dosya için yeterli süre (yaklaşık 1 MB/s hız varsayımıyla)
        xhr.timeout = 600000; // 10 dakika
        
        // Upload progress tracking (0-30% arası)
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const uploadPercent = Math.round((e.loaded / e.total) * 30);
            setUploadProgress(uploadPercent);
          }
        });

        xhr.onload = () => {
          if (xhr.status === 202) {
            try {
              const uploadData = JSON.parse(xhr.responseText);
              jobId = uploadData.jobId;
              if (!jobId) {
                reject(new Error('Job ID not received from server'));
                return;
              }
              setProcessingJobId(jobId);
              setUploadProgress(30); // Upload tamamlandı (%30)
              toast.info('File uploaded. Processing started...');
              resolve(jobId);
            } catch (parseErr) {
              reject(new Error('Failed to parse server response'));
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              reject(new Error(errorData.error || 'Upload failed'));
            } catch {
              reject(new Error(`Upload failed: ${xhr.statusText}`));
            }
          }
        };

        xhr.onerror = () => {
          reject(new Error('Network error during upload'));
        };
        
        xhr.ontimeout = () => {
          reject(new Error('Upload timeout. The file might be too large or connection is too slow. Please try again.'));
        };

        xhr.open('POST', `${EDEFTER_API_URL}/upload-xml-async`);
        const token = AuthService.getAuthToken();
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        xhr.send(formData);
      });

    // Wait for upload to complete (jobId is needed for status polling)
    await uploadPromise;
    uploadEndTime = performance.now();
    const uploadDuration = ((uploadEndTime - uploadStartTime) / 1000).toFixed(2);
    console.log(`[TIMING] ✅ Dosya yükleme tamamlandı: ${uploadDuration} saniye (${(uploadDuration / 60).toFixed(2)} dakika)`);

    // Status polling ile işlemin tamamlanmasını bekle
    processingStartTime = performance.now();
    console.log('[TIMING] ⚙️ XML işleme (parsing) başladı...');
    let jobStatus = null;
    let pollCount = 0;
    const maxPolls = 600; // 5 dakika timeout (0.5 saniye * 600 = 5 dakika)
    
    while (pollCount < maxPolls) {
      await new Promise(resolve => setTimeout(resolve, 500)); // 0.5 saniye bekle
      
      try {
        const statusResponse = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/upload-xml-async/${jobId}/status`);
        
        if (!statusResponse.ok) {
          throw new Error('Failed to check job status');
        }

        jobStatus = await statusResponse.json();
        setProcessingStatus(jobStatus.status);
        
        // Progress göster (30-90% arası, status'e göre)
        if (jobStatus.status === 'PENDING') {
          setUploadProgress(30 + Math.min(pollCount * 0.1, 10)); // 30-40%
        } else if (jobStatus.status === 'PROCESSING') {
          setUploadProgress(40 + Math.min(pollCount * 0.3, 50)); // 40-90%
        }

        if (jobStatus.status === 'COMPLETED') {
          setUploadProgress(95);
          break;
        } else if (jobStatus.status === 'FAILED') {
          throw new Error(jobStatus.errorMessage || 'Processing failed');
        }
        
        pollCount++;
      } catch (statusErr) {
        console.error('Error checking job status:', statusErr);
        // Status kontrolü hatası, devam et (network sorunları olabilir)
      }
    }

    if (!jobStatus || jobStatus.status !== 'COMPLETED') {
      processingEndTime = performance.now();
      const processingDuration = ((processingEndTime - processingStartTime) / 1000).toFixed(2);
      console.log(`[TIMING] ❌ XML işleme (parsing) hata ile sonlandı: ${processingDuration} saniye (${(processingDuration / 60).toFixed(2)} dakika)`);
      if (pollCount >= maxPolls) {
        throw new Error('Processing timeout. Please check status later.');
      } else {
        throw new Error(jobStatus?.errorMessage || 'Processing failed');
      }
    }

    processingEndTime = performance.now();
    const processingDuration = ((processingEndTime - processingStartTime) / 1000).toFixed(2);
    console.log(`[TIMING] ✅ XML işleme (parsing) tamamlandı: ${processingDuration} saniye (${(processingDuration / 60).toFixed(2)} dakika)`);

    const documentId = jobStatus.documentInfoId;
    console.log('[COMPANY] Document ID from job status:', documentId);
    
    // Wait for company info extraction to complete (if not already done)
    const extractedCompanyInfo = await companyInfoExtractionPromise;
    
    setUploadProgress(95);
    setSavedDocumentId(documentId);
    
    // Company bilgilerini oluştur
    try {
      let companyInfo = extractedCompanyInfo;
      console.log('[COMPANY] Company info from XML:', companyInfo);
      
      // Company bilgileri XML'den çıkarılmış olmalı (companyInfoFromXml)
      
      // Eğer company bilgileri varsa ve current user varsa, önce kontrol et sonra oluştur
      console.log('[COMPANY] Final company info check:', companyInfo);
      if (companyInfo && companyInfo.name && companyInfo.vergiKimlikNo) {
        const currentUser = AuthService.getCurrentUser();
        console.log('[COMPANY] Current user:', currentUser);
        if (currentUser?.id) {
          try {
            const vergiKimlikNo = companyInfo.vergiKimlikNo.trim();
            console.log('[COMPANY] Checking if company exists with tax number:', vergiKimlikNo);
            
            // Önce company'nin zaten var olup olmadığını kontrol et
            const checkResult = await CompanyService.checkCompanyExists(currentUser.id, vergiKimlikNo);
            console.log('[COMPANY] Check result:', checkResult);
            
            if (checkResult.success && checkResult.exists) {
              console.log('[COMPANY] Company already exists:', checkResult.data);
              // Company zaten var, hata verme
            } else {
              // Company yok, oluştur
              const companyData = {
                userId: currentUser.id,
                name: companyInfo.name.trim(),
                vergiKimlikNo: vergiKimlikNo,
                vergiDairesi: companyInfo.vergiDairesi?.trim() || null,
                naceCode: companyInfo.naceCode || null, // Already parsed as integer in XML parsing
                employeeCount: companyInfo.employeeCount ? parseInt(companyInfo.employeeCount) : null,
                phone: companyInfo.phone?.trim() || null,
                address: companyInfo.address?.trim() || null,
                email: companyInfo.email?.trim() || null,
                website: companyInfo.website?.trim() || null,
                status: 1,
              };
              
              console.log('[COMPANY] Creating company with data:', JSON.stringify(companyData, null, 2));
              const companyResult = await CompanyService.createCompany(companyData);
              console.log('[COMPANY] Company creation result:', companyResult);
              if (companyResult.success) {
                console.log('[COMPANY] Company created successfully from XML:', companyResult.data);
                toast.success(`Company "${companyInfo.name}" created successfully!`);
              } else {
                console.error('[COMPANY] Company creation failed:', companyResult.error);
                toast.error(`Company creation failed: ${companyResult.error}`);
              }
            }
          } catch (companyErr) {
            console.error('[COMPANY] Error processing company from XML:', companyErr);
            console.error('[COMPANY] Error stack:', companyErr.stack);
            toast.error(`Error creating company: ${companyErr.message}`);
          }
        } else {
          console.error('[COMPANY] Current user not found or missing id. User:', currentUser);
          toast.error('User information not found. Please login again.');
        }
      } else {
        console.error('[COMPANY] Company info not found or incomplete');
        console.error('[COMPANY] Company info object:', companyInfo);
        console.error('[COMPANY] Has name?', companyInfo?.name);
        console.error('[COMPANY] Has vergiKimlikNo?', companyInfo?.vergiKimlikNo);
        // Removed toast.warn - no warning message for missing company info
      }
    } catch (err) {
      console.error('Error processing company info:', err);
      // Hata olsa bile devam et
    }
    
    // Automatically calculate mizan after file upload is completed
    try {
      setCalculatingMizan(true);
      mizanStartTime = performance.now();
      console.log('[TIMING] 🧮 Mizan (Trial Balance) hesaplama başladı...');
      console.log('[MIZAN] Starting automatic mizan calculation for documentId:', documentId);
      
      const calculateResponse = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/calculate-mizan/${documentId}`, {
        method: 'POST',
      });

      if (!calculateResponse.ok) {
        const errorData = await calculateResponse.json().catch(() => ({ error: 'Failed to calculate trial balance' }));
        throw new Error(errorData.error || 'Failed to calculate trial balance');
      }

      const calculateData = await calculateResponse.json();
      if (!calculateData.success) {
        throw new Error(calculateData.error || 'Failed to calculate trial balance');
      }

      console.log('[MIZAN] Mizan calculated successfully');

      // Fetch mizan data
      const mizanResponse = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/mizan-by-document?documentId=${documentId}`);
      if (mizanResponse.ok) {
        const mizanData = await mizanResponse.json();
        setMizanData(mizanData);
        setActiveTab(0); // Switch to Trial Balance tab
      }
      
      setUploadProgress(100);
      toast.success('File successfully saved and trial balance calculated!');
      onUploadSuccess?.({ documentId });
      
      // Zaman ölçümü - başarılı tamamlanma
      mizanEndTime = performance.now();
      const mizanDuration = ((mizanEndTime - mizanStartTime) / 1000).toFixed(2);
      console.log(`[TIMING] ✅ Mizan (Trial Balance) hesaplama tamamlandı: ${mizanDuration} saniye (${(mizanDuration / 60).toFixed(2)} dakika)`);
      
      const endTime = performance.now();
      const durationSeconds = ((endTime - startTime) / 1000).toFixed(2);
      console.log('[TIMING] ========================================');
      console.log(`[TIMING] ✅ TOPLAM SÜRE: ${durationSeconds} saniye (${((endTime - startTime) / 1000 / 60).toFixed(2)} dakika)`);
      console.log(`[TIMING]   - Dosya Yükleme: ${uploadDuration} saniye`);
      console.log(`[TIMING]   - XML İşleme: ${processingDuration} saniye`);
      console.log(`[TIMING]   - Mizan Hesaplama: ${mizanDuration} saniye`);
      console.log(`[TIMING] Başlangıç: ${new Date(startTime).toISOString()}, Bitiş: ${new Date(endTime).toISOString()}`);
      console.log('[TIMING] ========================================');
    } catch (mizanErr) {
      console.error('[MIZAN] Error calculating mizan:', mizanErr);
      // Don't show error toast - just log it, file upload was successful
      setUploadProgress(100);
      toast.success('File successfully saved!');
      onUploadSuccess?.({ documentId });
      // Optionally show a warning about mizan calculation
      toast.warning('Trial balance calculation will be available shortly. You can manually calculate it if needed.');
      
      // Zaman ölçümü - mizan hesaplama hatası olsa bile upload başarılı
      mizanEndTime = performance.now();
      const mizanDuration = mizanStartTime ? ((mizanEndTime - mizanStartTime) / 1000).toFixed(2) : '0.00';
      const endTime = performance.now();
      const durationSeconds = ((endTime - startTime) / 1000).toFixed(2);
      console.log('[TIMING] ========================================');
      console.log(`[TIMING] ⚠️ TOPLAM SÜRE (mizan hesaplama hatası): ${durationSeconds} saniye (${((endTime - startTime) / 1000 / 60).toFixed(2)} dakika)`);
      console.log(`[TIMING]   - Dosya Yükleme: ${uploadDuration} saniye`);
      console.log(`[TIMING]   - XML İşleme: ${processingDuration} saniye`);
      console.log(`[TIMING]   - Mizan Hesaplama (HATA): ${mizanDuration} saniye`);
      console.log(`[TIMING] Başlangıç: ${new Date(startTime).toISOString()}, Bitiş: ${new Date(endTime).toISOString()}`);
      console.log('[TIMING] ========================================');
    } finally {
      setCalculatingMizan(false);
    }
    
    setSelectedFile(null);
    setUploadProgress(0);
    setProcessingJobId(null);
    setProcessingStatus(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'An error occurred while uploading the file.');
      toast.error(err.message || 'An error occurred while uploading the file.');
      setUploadProgress(0);
      setProcessingJobId(null);
      setProcessingStatus(null);
      
      // Zaman ölçümü - hata durumu
      const endTime = performance.now();
      const durationSeconds = ((endTime - startTime) / 1000).toFixed(2);
      const uploadDuration = uploadEndTime && uploadStartTime ? ((uploadEndTime - uploadStartTime) / 1000).toFixed(2) : (uploadStartTime ? ((endTime - uploadStartTime) / 1000).toFixed(2) : '0.00');
      const processingDuration = processingEndTime && processingStartTime ? ((processingEndTime - processingStartTime) / 1000).toFixed(2) : (processingStartTime ? ((endTime - processingStartTime) / 1000).toFixed(2) : '0.00');
      const mizanDuration = mizanEndTime && mizanStartTime ? ((mizanEndTime - mizanStartTime) / 1000).toFixed(2) : (mizanStartTime ? ((endTime - mizanStartTime) / 1000).toFixed(2) : '0.00');
      
      console.log('[TIMING] ========================================');
      console.log(`[TIMING] ❌ TOPLAM SÜRE (HATA): ${durationSeconds} saniye (${((endTime - startTime) / 1000 / 60).toFixed(2)} dakika)`);
      if (uploadStartTime) {
        console.log(`[TIMING]   - Dosya Yükleme: ${uploadDuration} saniye ${uploadEndTime ? '(tamamlandı)' : '(devam ediyordu)'}`);
      }
      if (processingStartTime) {
        console.log(`[TIMING]   - XML İşleme: ${processingDuration} saniye ${processingEndTime ? '(tamamlandı)' : '(devam ediyordu)'}`);
      }
      if (mizanStartTime) {
        console.log(`[TIMING]   - Mizan Hesaplama: ${mizanDuration} saniye ${mizanEndTime ? '(tamamlandı)' : '(devam ediyordu)'}`);
      }
      console.log(`[TIMING] Hata mesajı: ${err.message || 'Bilinmeyen hata'}`);
      console.log(`[TIMING] Başlangıç: ${new Date(startTime).toISOString()}, Bitiş: ${new Date(endTime).toISOString()}`);
      console.log('[TIMING] ========================================');
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateMizan = async () => {
    if (!savedDocumentId) {
      toast.error('Document ID not found');
      return;
    }

    setCalculatingMizan(true);
    setError(null);

    try {
      // Calculate mizan
      const calculateResponse = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/calculate-mizan/${savedDocumentId}`, {
        method: 'POST',
      });

      if (!calculateResponse.ok) {
        const errorData = await calculateResponse.json().catch(() => ({ error: 'Failed to calculate trial balance' }));
        throw new Error(errorData.error || 'Failed to calculate trial balance');
      }

      const calculateData = await calculateResponse.json();
      if (!calculateData.success) {
        throw new Error(calculateData.error || 'Failed to calculate trial balance');
      }

      toast.success('Trial balance calculated successfully!');

      // Fetch mizan data
      const mizanResponse = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/mizan-by-document?documentId=${savedDocumentId}`);
      if (mizanResponse.ok) {
        const mizanData = await mizanResponse.json();
        setMizanData(mizanData);
        setActiveTab(0); // Switch to Trial Balance tab
      }
    } catch (err) {
      console.error('Error calculating mizan:', err);
      setError(err.message || 'An error occurred while calculating trial balance');
      toast.error(err.message || 'An error occurred while calculating trial balance');
    } finally {
      setCalculatingMizan(false);
    }
  };

  const handleDownloadExcel = () => {
    if (!groupedMizanData || groupedMizanData.length === 0) {
      toast.error('Mizan verisi yok');
      return;
    }

    const toExcelNumber = (value) => {
      if (value === null || value === undefined) return 0;
      const n = parseFloat(value);
      return Number.isNaN(n) ? 0 : Math.round(n * 100) / 100;
    };

    const formatTrDate = (dateValue) => {
      if (!dateValue) return '';
      const d = new Date(dateValue);
      if (Number.isNaN(d.getTime())) return '';
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    };

    const startDateStr = formatTrDate(mizanData?.periodCoveredStart);
    const endDateStr = formatTrDate(mizanData?.periodCoveredEnd);

    const companyTitle =
      selectedCompany?.name ||
      selectedCompany?.title ||
      selectedCompany?.companyName ||
      selectedCompany?.unvan ||
      '';

    const headerRow = [
      'Hesap Kodu',
      'Hesap Alt Kodu',
      'Hesap Adı',
      'Hesap Detayı',
      'Borç Hareketleri Toplamı',
      'Alacak Hareketleri Toplamı',
      'Borç Bakiyesi',
      'Alacak Bakiyesi',
    ];

    const rows = [
      ['MİZAN', '', '', '', '', '', '', ''],
      [companyTitle, '', '', '', '', '', '', ''],
      ['Dönem :', (startDateStr && endDateStr) ? `${startDateStr}-${endDateStr}` : '', '', '', '', '', '', ''],
      ['Tarih Aralığı :', (startDateStr && endDateStr) ? `${startDateStr}-${endDateStr}` : '', '', '', '', '', '', ''],
      ['', '', '', '', '', '', '', ''],
      headerRow,
    ];

    groupedMizanData.forEach((group) => {
      const hasMultipleDetails = group.hasMultipleDetails;
      const hasAnyDetail = group.subRows.length > 0;

      if (!hasAnyDetail || !hasMultipleDetails) {
        const rowData = group.subRows[0] || group.baseRow || {
          hesapKodu: group.accountCode,
          subId: '',
          mainDescription: group.mainDescription,
          subDescription: '',
          borc: group.totalBorc,
          alacak: group.totalAlacak,
          borcBakiyesi: group.totalBorcBakiyesi,
          alacakBakiyesi: group.totalAlacakBakiyesi,
        };

        const hesapKodu = (rowData.hesapKodu || group.accountCode || '').trim();
        const subId = (rowData.subId || '').trim();

        rows.push([
          hesapKodu,
          subId && subId !== hesapKodu ? subId : hesapKodu,
          rowData.mainDescription || group.mainDescription,
          rowData.subDescription || '',
          toExcelNumber(rowData.borc ?? group.totalBorc),
          toExcelNumber(rowData.alacak ?? group.totalAlacak),
          toExcelNumber(rowData.borcBakiyesi ?? group.totalBorcBakiyesi),
          toExcelNumber(rowData.alacakBakiyesi ?? group.totalAlacakBakiyesi),
        ]);
        return;
      }

      // Total satırı (Sub Code = Account Code)
      rows.push([
        group.accountCode,
        group.accountCode,
        group.mainDescription,
        'Toplam',
        toExcelNumber(group.totalBorc),
        toExcelNumber(group.totalAlacak),
        toExcelNumber(group.totalBorcBakiyesi),
        toExcelNumber(group.totalAlacakBakiyesi),
      ]);

      group.subRows.forEach((row) => {
        const hKodu = (row.hesapKodu || '').trim();
        const sId = (row.subId || '').trim();
        rows.push([
          hKodu,
          sId && sId !== hKodu ? sId : hKodu,
          row.mainDescription || '',
          row.subDescription || '',
          toExcelNumber(row.borc),
          toExcelNumber(row.alacak),
          toExcelNumber(row.borcBakiyesi),
          toExcelNumber(row.alacakBakiyesi),
        ]);
      });
    });

    rows.push([
      'TOPLAM',
      '',
      '',
      '',
      toExcelNumber(mizanTotals.totalBorc),
      toExcelNumber(mizanTotals.totalAlacak),
      toExcelNumber(mizanTotals.totalBorcBakiyesi),
      toExcelNumber(mizanTotals.totalAlacakBakiyesi),
    ]);

    const ws = XLSX.utils.aoa_to_sheet(rows);

    // Header satırını (6. satır) text olarak sabitle
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let c = range.s.c; c <= range.e.c; c++) {
      const ref = XLSX.utils.encode_cell({ r: 5, c });
      if (ws[ref]) {
        ws[ref].t = 's';
        ws[ref].z = '@';
        if (typeof ws[ref].v !== 'string') ws[ref].v = String(ws[ref].v ?? '');
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Mizan');

    // Dosya adı: vergikimlikno_yıl_ay (senin kodun ile aynı)
    const vkn = (taxId || 'export').replace(/[^a-zA-Z0-9]/g, '');
    let filename = 'mizan';
    if (mizanData?.periodCoveredStart) {
      const d = new Date(mizanData.periodCoveredStart);
      const y = d.getFullYear();
      const ay = String(d.getMonth() + 1).padStart(2, '0');
      filename = `${vkn}_${y}_${ay}`;
    } else {
      filename = `${vkn}_mizan`;
    }

    XLSX.writeFile(wb, `${filename}.xlsx`);
    toast.success('Excel indirildi.');
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0,00';
    const number = parseFloat(value);
    if (isNaN(number)) return '0,00';
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  // Group mizan data by account code (for nested table structure)
  const groupedMizanData = useMemo(() => {
    if (!mizanData || !mizanData.rows) return null;

    const groups = new Map();

    mizanData.rows.forEach((row) => {
      const accountCode = row.hesapKodu || '';
      if (!groups.has(accountCode)) {
        groups.set(accountCode, {
          accountCode,
          mainDescription: row.mainDescription || '',
          baseRow: null,
          subRows: [],
        });
      }

      const group = groups.get(accountCode);
      if (!group.mainDescription && row.mainDescription) {
        group.mainDescription = row.mainDescription;
      }

      // Sadece gerçek alt hesap varsa iç içe tabloya koy (Sub Code !== Account Code)
      const hesapKodu = (row.hesapKodu || '').trim();
      const subId = (row.subId || '').trim();
      if (subId && subId !== hesapKodu) {
        group.subRows.push(row);
      } else {
        group.baseRow = row;
      }
    });

    return Array.from(groups.values())
      .map((group) => {
        const totalsFromSubRows = group.subRows.reduce(
          (acc, row) => {
            acc.borc += parseFloat(row.borc || 0);
            acc.alacak += parseFloat(row.alacak || 0);
            acc.borcBakiyesi += parseFloat(row.borcBakiyesi || 0);
            acc.alacakBakiyesi += parseFloat(row.alacakBakiyesi || 0);
            return acc;
          },
          { borc: 0, alacak: 0, borcBakiyesi: 0, alacakBakiyesi: 0 }
        );

        const hasMultipleDetails = group.subRows.length > 1;
        const hasAnyDetail = group.subRows.length > 0;

        const totalBorc = hasAnyDetail ? totalsFromSubRows.borc : (group.baseRow?.borc || 0);
        const totalAlacak = hasAnyDetail ? totalsFromSubRows.alacak : (group.baseRow?.alacak || 0);
        const fark = totalBorc - totalAlacak;
        const totalBorcBakiyesi = fark > 0 ? fark : 0;
        const totalAlacakBakiyesi = fark < 0 ? -fark : 0;

        return {
          ...group,
          totalBorc,
          totalAlacak,
          totalBorcBakiyesi,
          totalAlacakBakiyesi,
          hasMultipleDetails,
        };
      })
      .sort((a, b) => {
        return (a.accountCode || '').localeCompare(b.accountCode || '');
      });
  }, [mizanData]);

  const mizanTotals = useMemo(() => {
    if (!groupedMizanData || groupedMizanData.length === 0) {
      return { totalBorc: 0, totalAlacak: 0, totalBorcBakiyesi: 0, totalAlacakBakiyesi: 0 };
    }
    return groupedMizanData.reduce(
      (acc, group) => {
        acc.totalBorc += parseFloat(group.totalBorc) || 0;
        acc.totalAlacak += parseFloat(group.totalAlacak) || 0;
        acc.totalBorcBakiyesi += parseFloat(group.totalBorcBakiyesi) || 0;
        acc.totalAlacakBakiyesi += parseFloat(group.totalAlacakBakiyesi) || 0;
        return acc;
      },
      { totalBorc: 0, totalAlacak: 0, totalBorcBakiyesi: 0, totalAlacakBakiyesi: 0 }
    );
  }, [groupedMizanData]);

  const handleToggleMizanRow = (accountCode) => {
    const newExpanded = new Set(expandedMizanRows);
    if (newExpanded.has(accountCode)) {
      newExpanded.delete(accountCode);
    } else {
      newExpanded.add(accountCode);
    }
    setExpandedMizanRows(newExpanded);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Fetch entry headers when showJournal is true and savedDocumentId exists
  useEffect(() => {
    if (!showJournal || !savedDocumentId) {
      setEntryHeaders([]);
      setEntryDetailsCache(new Map());
      setTotalPages(0);
      setTotalElements(0);
      return;
    }

    const fetchEntryHeaders = async () => {
      try {
        setLoadingJournal(true);
        const pageIndex = journalPage - 1; // Backend 0-based, frontend 1-based
        const response = await AuthService.authenticatedFetch(
          `${EDEFTER_API_URL}/journal-entries-by-document-paginated?documentId=${savedDocumentId}&page=${pageIndex}&size=${rowsPerPage}`
        );
        
        if (response.ok) {
          const data = await response.json();
          if (data.error) {
            console.error('[Journal] Backend error:', data.error);
            return;
          }
          
          setEntryHeaders(data.entryHeaders || []);
          setTotalPages(data.totalPages || 0);
          setTotalElements(data.totalElements || 0);
        } else {
          const errorText = await response.text();
          console.error('[Journal] Error response:', errorText);
        }
      } catch (err) {
        console.error('Error fetching entry headers:', err);
      } finally {
        setLoadingJournal(false);
      }
    };

    fetchEntryHeaders();
  }, [showJournal, savedDocumentId, journalPage, rowsPerPage]);

  // Fetch entry details when a row is expanded
  const fetchEntryDetails = async (entryHeaderId) => {
    if (entryDetailsCache.has(entryHeaderId) || loadingDetails.has(entryHeaderId)) {
      return;
    }

    try {
      setLoadingDetails(prev => new Set(prev).add(entryHeaderId));
      const response = await AuthService.authenticatedFetch(
        `${EDEFTER_API_URL}/journal-entry-details?entryHeaderId=${entryHeaderId}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.error) {
          console.error('[Journal] Backend error:', data.error);
          return;
        }
        
        // Flatten entry details
        const flattenedDetails = [];
        if (data.entryHeader?.entryDetails && Array.isArray(data.entryHeader.entryDetails)) {
          data.entryHeader.entryDetails.forEach((detail) => {
            if (detail.accounts && Array.isArray(detail.accounts) && detail.accounts.length > 0) {
              detail.accounts.forEach((account) => {
                flattenedDetails.push({
                  entryDetailId: detail.id,
                  lineNumber: detail.lineNumber,
                  postingDate: detail.postingDate,
                  amount: detail.amount,
                  debitCreditCode: detail.debitCreditCode,
                  documentType: detail.documentType,
                  documentNumber: detail.documentNumber,
                  documentDate: detail.documentDate,
                  detailComment: detail.detailComment,
                  accountMainId: account.mainId,
                  accountMainDescription: account.mainDescription,
                  accountSubId: account.accountSub?.subId || null,
                  accountSubDescription: account.accountSub?.subDescription || null,
                });
              });
            } else {
              flattenedDetails.push({
                entryDetailId: detail.id,
                lineNumber: detail.lineNumber,
                postingDate: detail.postingDate,
                amount: detail.amount,
                debitCreditCode: detail.debitCreditCode,
                documentType: detail.documentType,
                documentNumber: detail.documentNumber,
                documentDate: detail.documentDate,
                detailComment: detail.detailComment,
                accountMainId: null,
                accountMainDescription: null,
                accountSubId: null,
                accountSubDescription: null,
              });
            }
          });
        }
        
        setEntryDetailsCache(prev => new Map(prev).set(entryHeaderId, flattenedDetails));
      }
    } catch (err) {
      console.error('Error fetching entry details:', err);
    } finally {
      setLoadingDetails(prev => {
        const newSet = new Set(prev);
        newSet.delete(entryHeaderId);
        return newSet;
      });
    }
  };

  const handleToggleEntry = (headerId) => {
    const newExpanded = new Set(expandedEntries);
    if (newExpanded.has(headerId)) {
      newExpanded.delete(headerId);
    } else {
      newExpanded.add(headerId);
      if (!entryDetailsCache.has(headerId)) {
        fetchEntryDetails(headerId);
      }
    }
    setExpandedEntries(newExpanded);
  };

  const handleViewJournalEntries = () => {
    setShowJournal(true);
    setJournalPage(1);
    setExpandedEntries(new Set());
    setEntryDetailsCache(new Map());
    setActiveTab(1);
  };

  const handlePreviewTabClick = (tabIndex) => {
    if (tabIndex === 0 && (!mizanData?.rows?.length)) return;
    if (tabIndex === 1 && (!showJournal || !savedDocumentId)) return;
    setActiveTab(tabIndex);
    if (tabIndex === 1 && !showJournal) {
      setShowJournal(true);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // If switching to Journal Entries tab, ensure showJournal is true
    if (newValue === 1 && !showJournal) {
      setShowJournal(true);
    }
  };

  // Memoized paginated groups
  const paginatedGroups = useMemo(() => {
    const groups = entryHeaders.map(header => {
      const allDetails = entryDetailsCache.get(header.id) || [];
      const isExpanded = expandedEntries.has(header.id);
      
      const detailTotalDebit = allDetails.reduce((sum, detail) => {
        return sum + (detail.debitCreditCode === 'D' ? parseFloat(detail.amount || 0) : 0);
      }, 0);
      const detailTotalCredit = allDetails.reduce((sum, detail) => {
        return sum + (detail.debitCreditCode === 'C' ? parseFloat(detail.amount || 0) : 0);
      }, 0);
      
      return {
        headerId: header.id,
        entryNumber: header.entryNumber,
        entryComment: header.entryComment,
        enteredBy: header.enteredBy,
        enteredDate: header.enteredDate,
        totalDebit: header.totalDebit,
        totalCredit: header.totalCredit,
        details: allDetails,
        detailTotalDebit: detailTotalDebit,
        detailTotalCredit: detailTotalCredit,
        isExpanded: isExpanded,
      };
    });
    
    return groups;
  }, [entryHeaders, entryDetailsCache, expandedEntries]);

  if (asModal && !open) return null;

  const progressLabel =
    uploadProgress < 30
      ? `${t('edefter.uploadProgressUploading')} ${uploadProgress}%`
      : processingStatus === 'PENDING'
        ? `${t('edefter.uploadProgressStarting')} ${uploadProgress}%`
        : processingStatus === 'PROCESSING'
          ? `${t('edefter.uploadProgressProcessing')} ${uploadProgress}%`
          : calculatingMizan
            ? `${t('edefter.uploadProgressMizan')} ${uploadProgress}%`
            : uploadProgress >= 100
              ? t('edefter.uploadProgressDone')
              : `${t('edefter.uploadProgressProcessing')} ${uploadProgress}%`;

  const showPreview = Boolean(
    savedDocumentId || (mizanData?.rows?.length > 0) || (showJournal && savedDocumentId)
  );

  const previewTabClass = (tabIndex, disabled = false) =>
    `px-4 py-1.5 text-xs font-bold rounded-md transition-all whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${
      activeTab === tabIndex
        ? 'bg-white dark:bg-[#1C1C1E] shadow-sm text-gray-900 dark:text-white'
        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
    }`;

  const formContent = (
    <>
            {/* Error Alert */}
            {error && (
              asModal ? (
                <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/40 rounded-xl px-4 py-3 mb-4">
                  <p className="text-xs text-red-600 dark:text-red-400 font-medium flex-1">{error}</p>
                  <button type="button" onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )
            )}

            {/* Existing Document Warning */}
            {existingDocument && (
              <div className="mb-4 bg-white dark:bg-[#1C1C1E] rounded-2xl border border-gray-100 dark:border-white/10 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-[#24299d]/10 dark:bg-[#24299d]/20 flex items-center justify-center shrink-0">
                      <AlertTriangle size={16} className="text-[#24299d] dark:text-[#818cf8]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {t('edefter.existingDocumentTitle')}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">
                        {t('edefter.existingDocumentWarning')}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleDeleteExistingDocument}
                    disabled={deletingDocument}
                    className="shrink-0 self-start sm:self-center inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-white/5 hover:bg-red-50 hover:border-red-200 hover:text-red-600 dark:hover:bg-red-900/15 dark:hover:border-red-800/40 dark:hover:text-red-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deletingDocument ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : (
                      <Trash2 size={13} />
                    )}
                    {deletingDocument ? t('edefter.deleting') : t('edefter.deleteExisting')}
                  </button>
                </div>
              </div>
            )}

            {/* File Upload Section */}
            {(!asModal || !hasPreviewContent) && (
              asModal ? (
                <div className={`space-y-4 ${existingDocument ? 'opacity-50 pointer-events-none select-none' : ''}`}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xml"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  {!selectedFile ? (
                    <div
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onClick={handleUploadClick}
                      className="border-2 border-dashed border-gray-200 dark:border-white/10 rounded-2xl p-10 text-center cursor-pointer hover:border-[#24299d]/50 hover:bg-gray-50/80 dark:hover:bg-white/[0.02] transition-all"
                    >
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-14 h-14 bg-[#24299d]/10 dark:bg-[#24299d]/20 rounded-2xl flex items-center justify-center">
                          <UploadIcon size={28} className="text-[#24299d] dark:text-[#818cf8]" />
                        </div>
                        <div>
                          <p className="text-base font-semibold mb-1 text-gray-900 dark:text-white">{t('edefter.dragDropXml')}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{t('edefter.orClickToSelect')}</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">{t('edefter.xmlOnlyHint')}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('edefter.selectedFile')}</p>
                      <div className="flex items-center justify-between p-3.5 bg-white dark:bg-[#1C1C1E] border border-gray-100 dark:border-white/10 rounded-xl">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-white/5 flex items-center justify-center shrink-0">
                            <FileText size={18} className="text-gray-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate text-gray-900 dark:text-white">{selectedFile.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{(selectedFile.size / 1024).toFixed(1)} KB · XML</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                          className="ml-2 p-1.5 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors shrink-0"
                        >
                          <X size={14} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" />
                        </button>
                      </div>
                    </div>
                  )}
                  {loading && (
                    <div className="space-y-2">
                      <div className="h-2 rounded-full bg-gray-200 dark:bg-white/10 overflow-hidden">
                        <div
                          className="h-full bg-[#24299d] transition-all duration-300 rounded-full"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 text-center">{progressLabel}</p>
                    </div>
                  )}
                </div>
              ) : (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <CloudUpload sx={{ color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="600">
                  General Journal File (.xml)
                </Typography>
              </Box>

              <Paper
                sx={{
                  p: 4,
                  border: '2px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 3,
                  textAlign: 'center',
                  bgcolor: 'grey.50',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'grey.100',
                    borderColor: 'primary.dark',
                  },
                }}
                onClick={handleUploadClick}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xml"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" color="primary" gutterBottom>
                  {selectedFile ? selectedFile.name : 'Upload XML File'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedFile ? 'File selected. Click the button to upload.' : 'Upload General Journal file in XML format'}
                </Typography>
              </Paper>

              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleSubmit}
                    disabled={loading || !selectedFile}
                    sx={{
                      minWidth: 200,
                      py: 1.5,
                      color: 'white',
                      borderRadius: 3,
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '1rem',
                      background: 'linear-gradient(135deg, rgba(16, 38, 166) 0%, rgba(14, 33, 148) 50%, rgba(12, 28, 130) 100%)',
                      boxShadow: '0 4px 15px rgba(16, 38, 166, 0.4)',
                      '&:hover': {
                        color: 'white',
                        background: 'linear-gradient(135deg, rgba(20, 45, 180, 1) 0%, rgba(18, 40, 170, 1) 50%, rgba(16, 38, 166) 100%)',
                        boxShadow: '0 6px 20px rgba(16, 38, 166, 0.6)',
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        background: 'rgba(16, 38, 166, 0.3)',
                      }
                    }}
                  >
                    {loading ? `Uploading...` : 'Upload and Generate Trial Balance'}
                  </Button>
                </Box>
                {loading && (
                  <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
                    <LinearProgress 
                      variant="determinate"
                      value={uploadProgress} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                        }
                      }} 
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                      {progressLabel}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
              )
            )}

            {/* Success banner + preview tabs */}
            {showPreview && (
              <div className="space-y-4">
                {savedDocumentId && !loading && !existingDocument && (
                  <div className="flex items-start gap-3 p-4 rounded-2xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/40">
                    <CheckCircle2 size={20} className="text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                        File Successfully Saved
                      </p>
                      <p className="text-xs text-green-700/80 dark:text-green-400/80 mt-0.5">
                        {calculatingMizan
                          ? 'Trial balance is being calculated...'
                          : 'Your file has been processed and saved. Trial balance has been calculated automatically. You can view journal entries below.'}
                      </p>
                    </div>
                    {calculatingMizan && (
                      <Loader2 size={18} className="text-[#24299d] dark:text-[#818cf8] animate-spin shrink-0" />
                    )}
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="bg-gray-200 dark:bg-white/10 p-1 rounded-lg flex flex-wrap">
                    <button
                      type="button"
                      onClick={() => handlePreviewTabClick(0)}
                      disabled={!mizanData?.rows?.length}
                      className={previewTabClass(0, !mizanData?.rows?.length)}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <FileSpreadsheet size={13} />
                        Trial Balance
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePreviewTabClick(1)}
                      disabled={!showJournal || !savedDocumentId}
                      className={previewTabClass(1, !showJournal || !savedDocumentId)}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <BookOpen size={13} />
                        Journal Entries
                      </span>
                    </button>
                  </div>
                  {activeTab === 0 && mizanData?.rows?.length > 0 && (
                    <button
                      type="button"
                      onClick={handleDownloadExcel}
                      className="flex items-center gap-2 px-4 py-2 bg-[#24299d] hover:bg-[#1b2080] text-white rounded-xl text-sm font-semibold transition-all shadow-sm hover:shadow-md active:scale-95 whitespace-nowrap"
                    >
                      <Download size={16} />
                      Download Excel
                    </button>
                  )}
                </div>

                {!asModal && !showJournal && !calculatingMizan && savedDocumentId && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleViewJournalEntries}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm border-2 border-[#24299d] text-[#24299d] dark:border-[#818cf8] dark:text-[#818cf8] hover:bg-[#24299d]/5 dark:hover:bg-[#818cf8]/10 transition-all"
                    >
                      <BookOpen size={16} />
                      View Journal Entries
                    </button>
                  </div>
                )}

                {activeTab === 0 && mizanData?.periodCoveredStart && mizanData?.periodCoveredEnd && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5 -mt-1">
                    <Calendar size={13} />
                    {formatDate(mizanData.periodCoveredStart)} - {formatDate(mizanData.periodCoveredEnd)}
                  </p>
                )}

                {/* Trial Balance Tab Panel */}
                {activeTab === 0 && mizanData && mizanData.rows && mizanData.rows.length > 0 && (
                  <div className="rounded-2xl border border-gray-100 dark:border-white/5 overflow-hidden">
                      <TableContainer sx={getTableContainerSx(darkMode, { maxHeight: asModal ? '50vh' : 'calc(100vh - 200px)' })}>
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow sx={getTableHeadRowSx(darkMode)}>
                              <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap', width: 50 }}>
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                                Account Code
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                                Sub Code
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                                Account Name
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                                Account Detail Name
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                                Debit
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                                Credit
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                                Debit Balance
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                                Credit Balance
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {groupedMizanData ? (
                              // Nested table structure (like TrialBalance page)
                              groupedMizanData.map((group, groupIndex) => {
                                const isExpanded = expandedMizanRows.has(group.accountCode);
                                const hasAnyDetail = group.subRows.length > 0;

                                return (
                                  <React.Fragment key={`group-${group.accountCode}-${groupIndex}`}>
                                    {/* Ana satır (toplam) */}
                                    <TableRow 
                                      hover
                                      sx={{
                                        '&:hover': {
                                          backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                        },
                                        borderLeft: '4px solid transparent',
                                        backgroundColor: 'rgba(25, 118, 210, 0.02)'
                                      }}
                                    >
                                      <TableCell sx={{ width: 50 }}>
                                        {hasAnyDetail ? (
                                          <IconButton
                                            size="small"
                                            onClick={() => handleToggleMizanRow(group.accountCode)}
                                            sx={{ p: 0.5 }}
                                          >
                                            {isExpanded ? (
                                              <ExpandMore sx={{ fontSize: 20 }} />
                                            ) : (
                                              <ChevronRight sx={{ fontSize: 20 }} />
                                            )}
                                          </IconButton>
                                        ) : null}
                                      </TableCell>
                                      <TableCell sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                        {group.accountCode}
                                      </TableCell>
                                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                        {/* Top-level satırda sub code gösterme */}
                                      </TableCell>
                                      <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem', fontWeight: 600 }}>
                                        {group.mainDescription}
                                      </TableCell>
                                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                        {/* Top-level satırda sub description gösterme */}
                                      </TableCell>
                                      <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                        {formatNumber(group.totalBorc)}
                                      </TableCell>
                                      <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                        {formatNumber(group.totalAlacak)}
                                      </TableCell>
                                      <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                        {formatNumber(group.totalBorcBakiyesi)}
                                      </TableCell>
                                      <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                        {formatNumber(group.totalAlacakBakiyesi)}
                                      </TableCell>
                                    </TableRow>

                                    {/* Detay satırları (Collapse içinde) */}
                                    {hasAnyDetail && (
                                      <TableRow>
                                        <TableCell colSpan={9} sx={{ py: 0, border: 'none' }}>
                                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                            <Box sx={{ margin: 0 }}>
                                              <Table size="small">
                                                <TableHead>
                                                  <TableRow sx={getNestedTableHeadRowSx(darkMode)}>
                                                    <TableCell sx={{ width: 50 }} />
                                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                      Account Code
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                      Sub Code
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                      Account Name
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                      Account Detail Name
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                      Debit
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                      Credit
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                      Debit Balance
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                      Credit Balance
                                                    </TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {group.subRows.map((row, rowIndex) => (
                                                    <TableRow 
                                                      key={`detail-${group.accountCode}-${row.subId}-${rowIndex}`}
                                                      sx={{
                                                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                        '&:hover': {
                                                          backgroundColor: 'rgba(25, 118, 210, 0.06)',
                                                        },
                                                      }}
                                                    >
                                                      <TableCell sx={{ width: 50, pl: 4 }}></TableCell>
                                                      <TableCell sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                                        {row.hesapKodu || ''}
                                                      </TableCell>
                                                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                                        {row.subId || ''}
                                                      </TableCell>
                                                      <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                                                        {row.mainDescription || ''}
                                                      </TableCell>
                                                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                                        {row.subDescription || ''}
                                                      </TableCell>
                                                      <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                                        {formatNumber(row.borc)}
                                                      </TableCell>
                                                      <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                                        {formatNumber(row.alacak)}
                                                      </TableCell>
                                                      <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                                        {formatNumber(row.borcBakiyesi)}
                                                      </TableCell>
                                                      <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                                        {formatNumber(row.alacakBakiyesi)}
                                                      </TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </Table>
                                            </Box>
                                          </Collapse>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </React.Fragment>
                                );
                              })
                            ) : (
                              // Fallback: Eğer groupedMizanData yoksa, normal tablo göster
                              mizanData.rows.map((row, index) => (
                                <TableRow 
                                  key={index}
                                  hover
                                  sx={{
                                    '&:hover': {
                                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                    },
                                    borderLeft: '4px solid transparent'
                                  }}
                                >
                                  <TableCell sx={{ width: 50 }}></TableCell>
                                  <TableCell sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                    {row.hesapKodu || ''}
                                  </TableCell>
                                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                    {row.subId || ''}
                                  </TableCell>
                                  <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                                    {row.mainDescription || ''}
                                  </TableCell>
                                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                    {row.subDescription || ''}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                    {formatNumber(row.borc)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                    {formatNumber(row.alacak)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                    {formatNumber(row.borcBakiyesi)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                    {formatNumber(row.alacakBakiyesi)}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                  </div>
                )}

                {/* Journal Entries Tab Panel */}
                {activeTab === 1 && showJournal && savedDocumentId && (
                  <div className="space-y-3">
                    <p className="text-xs text-gray-400 dark:text-gray-500 font-medium">
                      Journal Entries ({totalElements} total entries, page {journalPage} of {totalPages})
                    </p>

                    {loadingJournal ? (
                      <div className="flex flex-col items-center justify-center py-12 gap-3">
                        <Loader2 size={32} className="text-[#24299d] dark:text-[#818cf8] animate-spin" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">Loading journal entries...</p>
                      </div>
                    ) : entryHeaders.length > 0 ? (
                  <div className="rounded-2xl border border-gray-100 dark:border-white/5 overflow-hidden">
                    <TableContainer sx={getTableContainerSx(darkMode, { maxHeight: asModal ? '50vh' : 'calc(100vh - 300px)' })}>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow sx={getTableHeadRowSx(darkMode)}>
                            <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap', width: 50 }}>
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                              Entry #
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                              Date
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                              Comment
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                              Debit
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                              Credit
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedGroups.map((group) => {
                            const isExpanded = expandedEntries.has(group.headerId);
                            const isLoadingDetails = loadingDetails.has(group.headerId);
                            const hasDetails = group.details.length > 0;
                            const shouldShowDetails = isExpanded && (hasDetails || isLoadingDetails);

                            return (
                              <React.Fragment key={`entry-${group.headerId}`}>
                                <TableRow 
                                  hover
                                  sx={{
                                    '&:hover': {
                                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                    },
                                    borderLeft: '4px solid transparent',
                                  }}
                                >
                                  <TableCell sx={{ width: 50 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleToggleEntry(group.headerId)}
                                      sx={{ p: 0.5 }}
                                    >
                                      {isExpanded ? (
                                        <ExpandMore sx={{ fontSize: 20 }} />
                                      ) : (
                                        <ChevronRight sx={{ fontSize: 20 }} />
                                      )}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.85rem' }}>
                                    {group.entryNumber || '-'}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.85rem' }}>
                                    {formatDate(group.enteredDate)}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                    {group.entryComment || '-'}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: 'text.primary', fontSize: '0.85rem' }}>
                                    {formatNumber(group.totalDebit)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: 'text.primary', fontSize: '0.85rem' }}>
                                    {formatNumber(group.totalCredit)}
                                  </TableCell>
                                </TableRow>
                                
                                {shouldShowDetails && (
                                  <TableRow>
                                    <TableCell colSpan={6} sx={{ py: 0, border: 'none' }}>
                                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                        <Box sx={{ margin: 0 }}>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow sx={getNestedTableHeadRowSx(darkMode)}>
                                                <TableCell sx={{ width: 50 }} />
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                  Line #
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                  Posting Date
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                  Account Code
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                  Account Name
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                  Sub Account
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                  Debit
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                  Credit
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                  Comment
                                                </TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {isLoadingDetails ? (
                                                <TableRow>
                                                  <TableCell colSpan={9} sx={{ textAlign: 'center', py: 2 }}>
                                                    <CircularProgress size={20} />
                                                    <Typography variant="body2" sx={{ ml: 1, display: 'inline' }}>
                                                      Loading details...
                                                    </Typography>
                                                  </TableCell>
                                                </TableRow>
                                              ) : hasDetails ? (
                                                group.details.map((detail, idx) => (
                                                  <TableRow 
                                                    key={`detail-${detail.entryDetailId}-${idx}`}
                                                    sx={{
                                                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                      '&:hover': {
                                                        backgroundColor: 'rgba(25, 118, 210, 0.06)',
                                                      },
                                                    }}
                                                  >
                                                    <TableCell sx={{ width: 50, pl: 4 }}></TableCell>
                                                    <TableCell sx={{ fontSize: '0.8rem' }}>
                                                      {detail.lineNumber || '-'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.8rem' }}>
                                                      {formatDate(detail.postingDate)}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                                      {detail.accountMainId || '-'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.8rem' }}>
                                                      {detail.accountMainDescription || '-'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                                      {detail.accountSubId ? `${detail.accountSubId} - ${detail.accountSubDescription || ''}` : '-'}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                                      {detail.debitCreditCode === 'D' ? formatNumber(detail.amount) : '-'}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                                      {detail.debitCreditCode === 'C' ? formatNumber(detail.amount) : '-'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                                      {detail.detailComment || '-'}
                                                    </TableCell>
                                                  </TableRow>
                                                ))
                                              ) : (
                                                <TableRow>
                                                  <TableCell colSpan={9} sx={{ textAlign: 'center', py: 2 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                      No details found
                                                    </Typography>
                                                  </TableCell>
                                                </TableRow>
                                              )}
                                              {hasDetails && group.details.length > 0 && (() => {
                                                const totalsEqual = Math.abs(group.detailTotalDebit - group.detailTotalCredit) < 0.01;
                                                const totalColor = totalsEqual ? 'success.main' : 'error.main';
                                                return (
                                                  <TableRow sx={{ 
                                                    backgroundColor: darkMode ? '#252528' : 'rgba(25, 118, 210, 0.08)',
                                                    borderTop: '2px solid',
                                                    borderColor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(36, 41, 157, 0.2)',
                                                    '& td': {
                                                      fontWeight: 700,
                                                      fontSize: '0.85rem',
                                                    }
                                                  }}>
                                                    <TableCell sx={{ width: 50, pl: 4 }}></TableCell>
                                                    <TableCell colSpan={5} sx={{ fontWeight: 700, fontSize: '0.85rem', textAlign: 'right' }}>
                                                      Total:
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: totalColor, fontSize: '0.85rem' }}>
                                                      {formatNumber(group.detailTotalDebit)}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: totalColor, fontSize: '0.85rem' }}>
                                                      {formatNumber(group.detailTotalCredit)}
                                                    </TableCell>
                                                    <TableCell></TableCell>
                                                  </TableRow>
                                                );
                                              })()}
                                            </TableBody>
                                          </Table>
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {totalPages > 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <Pagination
                          count={totalPages}
                          page={journalPage}
                          onChange={(event, value) => {
                            setJournalPage(value);
                            setExpandedEntries(new Set());
                          }}
                          color="primary"
                          size="large"
                          showFirstButton
                          showLastButton
                        />
                      </Box>
                    )}
                  </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-sm text-gray-500 dark:text-gray-400">No journal entries found.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Empty State */}
            {!asModal && !savedDocumentId && !mizanData && !loading && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Box sx={{ display: 'inline-flex', p: 3, borderRadius: 3, backgroundColor: 'grey.100', mb: 2 }}>
                  <Description sx={{ fontSize: 48, color: 'grey.400' }} />
                </Box>
                <Typography variant="h6" sx={{ mb: 1, color: 'text.primary', fontSize: '1rem' }}>
                  No File Uploaded Yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Upload a General Journal XML file to view the trial balance report.
                </Typography>
              </Box>
            )}
    </>
  );

  if (asModal) {
    const isPreviewMode = hasPreviewContent;
    return createPortal(
      <div className={`fixed inset-0 backdrop-blur-sm z-[9999] flex items-center justify-center p-3 md:p-6 ${isPreviewMode ? 'bg-black/60' : 'bg-black/50'}`}>
        <div className={`bg-white dark:bg-[#1C1C1E] rounded-3xl shadow-2xl w-full flex flex-col ${isPreviewMode ? 'max-w-6xl h-[90vh]' : 'max-w-lg'}`}>
          <div className={`flex items-start justify-between px-6 py-4 border-b border-gray-100 dark:border-white/10 shrink-0 ${isPreviewMode ? '' : 'pt-6'}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center shrink-0">
                <UploadIcon size={18} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">
                  {isPreviewMode ? t('edefter.uploadPreviewHint') : t('edefter.uploadTitle')}
                </h3>
                {isPreviewMode ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {t('edefter.uploadTitle')}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 hidden sm:block">
                    {t('edefter.uploadSubtitle')}
                  </p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={handleModalClose}
              disabled={loading}
              className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-xl transition-colors disabled:opacity-40 shrink-0 ml-4"
            >
              <X size={20} className="text-gray-500 dark:text-gray-400" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 min-h-0">{formContent}</div>
          {!isPreviewMode && selectedFile && !loading && !existingDocument && (
            <div className="flex gap-3 px-6 pb-6 shrink-0">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !selectedFile}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-all shadow disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <UploadIcon size={18} />
                {t('edefter.uploadAndGenerate')}
              </button>
              <button
                type="button"
                onClick={handleModalClose}
                className="px-5 py-3 bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 rounded-xl font-semibold text-sm text-gray-700 dark:text-white transition-all"
              >
                {t('common.cancel')}
              </button>
            </div>
          )}
          {isPreviewMode && !loading && (
            <div className="flex gap-3 items-center px-6 py-4 border-t border-gray-100 dark:border-white/10 shrink-0">
              {!showJournal && !calculatingMizan && savedDocumentId && (
                <button
                  type="button"
                  onClick={handleViewJournalEntries}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm border-2 border-[#24299d] text-[#24299d] dark:border-[#818cf8] dark:text-[#818cf8] hover:bg-[#24299d]/5 dark:hover:bg-[#818cf8]/10 transition-all"
                >
                  <BookOpen size={16} />
                  View Journal Entries
                </button>
              )}
              <button
                type="button"
                onClick={handleModalClose}
                className="ml-auto px-6 py-2.5 rounded-xl bg-[#24299d] hover:bg-[#1b2080] text-white text-sm font-semibold transition-all shadow-sm"
              >
                {t('common.close')}
              </button>
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="bg-white dark:bg-[#1C1C1E] rounded-2xl md:rounded-[28px] shadow-sm border border-transparent dark:border-white/10 overflow-hidden">
        <div className="bg-gradient-to-r from-[#24299d] via-[#1e2385] to-[#1b2080] p-5 md:p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 border border-white/30 flex items-center justify-center shrink-0">
              <Description sx={{ fontSize: '1.75rem' }} />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-bold">{t('edefter.uploadTitle')}</h2>
              <p className="text-sm text-white/80 mt-0.5">{t('edefter.uploadSubtitle')}</p>
            </div>
          </div>
        </div>
        <div className="p-4 md:p-6">{formContent}</div>
      </div>
    </div>
  );
};

export default EdefterUpload;
